// Documents Corner v3 — full-workflow redesign per user directive (2026-02-17).
//
// Layout:
//   • Top-level tabs: "Documents" | "Editor Corner"
//   • Documents tab (audience feed):
//       - Sub-tabs: Current | Archived
//       - Card/List view toggle (replaces the old "New Document" banner CTA)
//       - Tag filter chips: All | Proposal | Guidelines | Company Policy | Other + search
//       - Cards show status, tag, Important, New(<15d), Unread, Re-Edited, read count
//   • Editor Corner tab (only visible to Master Admin + posting-eligible users):
//       - Sub-tabs: My Posts | Approval | Unassigned (MA only)
//       - Tab Access dialog stays (MA only)
//       - "New Document" button lives here (not in banner)
//
// Approval workflow (per new spec):
//   • MA post → published immediately (unless scheduled).
//   • Non-important non-MA post → status=pending_admin. Any admin (excluding
//     creator) approves → pending_master → published. 1-day auto-approve at
//     each stage. When MA acts, it flips to Published in one step.
//   • Important non-MA post → status=pending_master (skips admin), waits
//     indefinitely for MA.
//   • Reject requires mandatory remarks. Creator can Re-Edit & Resubmit;
//     the doc goes back into the appropriate pending queue.
//
// Deletion rules:
//   • MA — anything, anytime (draft, scheduled, published).
//   • Creator — own draft only.
//   • Nobody else can delete anything.

import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatError } from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { PageHeader, GradientCard } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { tabAccessibleFor, subtabAccessibleFor } from "../lib/modules";
import { toast } from "sonner";
import {
  Plus, Folder, Search, ShieldCheck,
  Inbox, Eye, XCircle, CheckCircle2, ShieldAlert, Star, RefreshCcw,
  Pencil, Trash2, Send,
} from "lucide-react";
import { fmtDateTimeIST } from "../lib/datetime";
import DocumentCard from "../components/documents/DocumentCard";
import NewDocDialog from "../components/documents/NewDocDialog";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import DocDetailDialog from "../components/documents/DocDetailDialog";
import { RejectDialog, ReassignDialog } from "../components/documents/DialogsSmall";
import {
  TAG_META, TAG_ORDER, STATUS_META, fmtCountdown,
} from "../components/documents/docConstants";

// ---------- Tab Access dialog (unchanged in behaviour — MA only) ----------

import UserPickerDialog from "../components/UserPickerDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { UserPlus, X } from "lucide-react";

const ROLE_OPTIONS = [
  "master_admin", "hr_admin", "sales_admin", "sales_team", "accounts_admin",
  "dispatch_admin", "it_admin", "crm_manager", "documentation_team",
  "trainer", "employee",
];
const roleLabel = (r) => ({
  master_admin: "Master Admin", hr_admin: "HR Admin", sales_admin: "Sales Admin",
  sales_team: "Sales Team", accounts_admin: "Accounts Admin",
  dispatch_admin: "Dispatch Admin", it_admin: "IT Admin",
  crm_manager: "CRM Manager", documentation_team: "Documentation",
  trainer: "Trainer", employee: "Employee",
}[r] || r);

function TabAccessDialog({ open, onClose, users, onSaved }) {
  const [grants, setGrants] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedIds, setPickedIds] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/access");
      setGrants(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { if (open) { load(); setPickedIds([]); } }, [open, load]);

  const grantBulk = async () => {
    if (pickedIds.length === 0) { toast.error("Pick at least one user"); return; }
    setBusy(true);
    let ok = 0; let fail = 0;
    for (const uid of pickedIds) {
      try {
        const fd = new FormData();
        fd.append("user_id", uid); fd.append("access_role", "editor");
        await api.post("/documents/access", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch { fail += 1; }
    }
    if (ok) toast.success(`Granted access to ${ok} user${ok > 1 ? "s" : ""}${fail ? ` · ${fail} failed` : ""}`);
    else toast.error("Failed to grant access");
    setPickedIds([]); await load(); onSaved?.(); setBusy(false);
  };
  const revoke = async (uid) => {
    if (!window.confirm("Revoke access?")) return;
    try { await api.delete(`/documents/access/${uid}`); toast.success("Revoked"); await load(); onSaved?.(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const changeRole = async (uid, current) => {
    const next = current === "admin" ? "editor" : "admin";
    try {
      const fd = new FormData();
      fd.append("user_id", uid); fd.append("access_role", next);
      await api.post("/documents/access", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Set to ${next}`); await load(); onSaved?.();
    } catch (e) { toast.error(formatError(e)); }
  };

  const grantedIds = useMemo(() => new Set(grants.map((g) => g.user_id)), [grants]);
  const availableUsers = useMemo(() => users.filter((u) => !grantedIds.has(u.id)), [users, grantedIds]);
  const pickedUsers = useMemo(
    () => pickedIds.map((id) => availableUsers.find((u) => u.id === id)).filter(Boolean),
    [pickedIds, availableUsers],
  );

  return (
    <>
      <Dialog open={open && !pickerOpen} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="doc-tab-access-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" /> Editor Corner · Tab Access
            </DialogTitle>
            <DialogDescription>
              Grant posting rights (Editor / Admin) on the Documents module to specific employees.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/60 space-y-3">
              <div className="text-[12px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">Grant access</div>
              <button
                type="button" onClick={() => setPickerOpen(true)}
                className="w-full text-left rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 hover:border-indigo-400"
                data-testid="doc-tab-access-open-picker"
              >
                {pickedIds.length === 0
                  ? <span className="text-slate-400">Click to select users…</span>
                  : <span className="text-slate-800"><strong className="text-indigo-700">{pickedIds.length}</strong> selected</span>}
                <span className="float-right text-slate-400">▸ Pick</span>
              </button>
              {pickedUsers.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {pickedUsers.slice(0, 8).map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200 px-2 py-0.5 text-[11px]">
                      {u.name}
                      <button onClick={() => setPickedIds((p) => p.filter((x) => x !== u.id))} className="hover:text-rose-600"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  {pickedUsers.length > 8 && <span className="text-[11px] text-slate-500 dark:text-slate-400 self-center">+{pickedUsers.length - 8} more</span>}
                </div>
              )}
              <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white/60 px-3 py-2 text-[11.5px] text-slate-600">
                All selected users are granted <strong className="text-indigo-700">Editor</strong> access by default. Promote to Admin later via the toggle.
              </div>
              <Button onClick={grantBulk} disabled={busy || pickedIds.length === 0} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="doc-tab-access-grant">
                <UserPlus className="w-4 h-4 mr-1.5" />
                {busy ? "Granting…" : `Grant Editor access to ${pickedIds.length || 0} user${pickedIds.length === 1 ? "" : "s"}`}
              </Button>
            </div>

            <div className="rounded-xl border border-slate-200">
              <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-800 text-[12px] uppercase tracking-wide text-slate-500 dark:text-slate-400 font-medium">Current grants ({grants.length})</div>
              <div className="max-h-[440px] overflow-y-auto divide-y">
                {grants.length === 0 && <div className="p-6 text-slate-400 dark:text-slate-500 text-[13px] text-center">No editors or admins granted yet.</div>}
                {grants.map((g) => {
                  const isAdmin = g.access_role === "admin";
                  return (
                    <div key={g.id} className="flex items-center justify-between gap-2 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[13.5px] font-semibold text-slate-900 dark:text-slate-100 truncate">{g.user_name}</div>
                          <Badge className={isAdmin ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-blue-100 text-blue-800 border-blue-200"}>
                            {isAdmin ? "Admin access" : "Editor access"}
                          </Badge>
                        </div>
                        <div className="text-[11.5px] text-slate-500 dark:text-slate-400 truncate">{g.user_email} · {roleLabel(g.user_role)}</div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button type="button" onClick={() => changeRole(g.user_id, g.access_role)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border transition-colors ${
                            isAdmin ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                                    : "bg-rose-100 text-rose-700 border-rose-400 hover:bg-rose-200"
                          }`} title={isAdmin ? "Admin — click to demote to Editor" : "Editor — click to promote to Admin"}>
                          <span className={`relative inline-flex h-3.5 w-6 items-center rounded-full ${isAdmin ? "bg-white/90" : "bg-rose-400"}`}>
                            <span className={`inline-block h-2.5 w-2.5 rounded-full ${isAdmin ? "translate-x-3 bg-emerald-600" : "translate-x-0.5 bg-white"}`} />
                          </span>
                          admin
                        </button>
                        <button onClick={() => revoke(g.user_id)} className="text-rose-500 hover:text-rose-700" title="Revoke"><X className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <UserPickerDialog
        open={pickerOpen} onClose={() => setPickerOpen(false)}
        onDone={(ids) => setPickedIds(ids)}
        users={availableUsers} initialSelectedIds={pickedIds}
        title="Select users to grant access" roleOptions={ROLE_OPTIONS}
      />
    </>
  );
}

// ---------- List row (compact horizontal table row) ----------

function ListRow({ d, currentUserId, isMaster, onOpen, onEdit, onRemove, onPublish, onResubmit, onApprove, onGoodToGo, onReject, onReassign, context = "feed" }) {
  const sMeta = STATUS_META[d.status] || STATUS_META.approved;
  const tagMeta = TAG_META[d.tag] || TAG_META.other;
  const tagLabel = d.tag === "other" && d.tag_custom ? d.tag_custom : tagMeta.label;
  const isCreator = (d.created_by_id || d.uploaded_by_id) === currentUserId;
  const canEdit = isMaster || (isCreator && (d.status === "draft" || d.status === "scheduled" || d.status === "rejected" || d.revise_pending));
  const canDelete = isMaster || (isCreator && d.status === "draft");
  const canResubmit = isCreator && d.status === "rejected";
  const showPublish = isCreator && (d.status === "draft" || d.status === "scheduled");
  const iAmReviewer = (d.reviewers || []).some((r) => r.user_id === currentUserId && !r.good_to_go);
  const canApprove = context === "approval" && (d.status === "pending_admin" || d.status === "pending_master");
  const canGoodToGo = (context === "review" || context === "approval") && d.status === "pending_review" && iAmReviewer;
  return (
    <div
      className="grid grid-cols-[minmax(0,3fr)_1fr_1fr_auto_auto_auto_auto] items-center gap-3 px-3 py-2.5 border-b border-slate-100 dark:border-slate-700 bg-white dark:bg-transparent hover:bg-indigo-50/40 dark:hover:bg-white/5"
      data-testid={`doc-list-row-${d.id}`}
    >
      <div className="min-w-0 flex items-center gap-2 cursor-pointer" onClick={() => onOpen(d)}>
        {d.is_important && <ShieldAlert className="w-3.5 h-3.5 text-rose-500 flex-shrink-0" />}
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 dark:text-white truncate flex items-center gap-1.5">
            {d.title}
            {d.is_new && <span className="text-[9px] uppercase font-bold bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-full px-1.5 py-0.5"><Star className="inline w-2 h-2 mr-0.5" />New</span>}
            {d.status === "approved" && !d.i_read && <span className="text-[9px] uppercase font-bold bg-yellow-100 text-yellow-800 border border-yellow-300 dark:bg-yellow-900/40 dark:text-yellow-300 dark:border-yellow-700 rounded-full px-1.5 py-0.5">Unread</span>}
            {(d.revision_count || 0) > 0 && <span className="text-[9px] uppercase font-bold bg-indigo-50 text-indigo-800 border border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700 rounded-full px-1.5 py-0.5"><RefreshCcw className="inline w-2 h-2 mr-0.5" />{d.revision_count}x</span>}
          </div>
          {d.description && <div className="text-[11px] text-slate-500 dark:text-slate-200 truncate">{d.description}</div>}
        </div>
      </div>
      <span className={`inline-flex items-center justify-center text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${tagMeta.cls} justify-self-start`}>{tagLabel}</span>
      <span className="text-[11.5px] text-slate-700 dark:text-white truncate">{d.created_by_name}</span>
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold border rounded-full px-1.5 py-0.5 ${sMeta.cls}`}>{sMeta.label}</span>
      <span className="inline-flex items-center gap-0.5 text-[11px] text-indigo-700 dark:text-indigo-300 font-semibold" title={`${d.read_count || 0} openers`}>
        <Eye className="w-3 h-3" /> {d.read_count || 0}
      </span>
      <span className="text-[10.5px] text-slate-500 dark:text-white font-mono">{fmtDateTimeIST(d.published_at || d.created_at)}</span>
      <div className="flex items-center gap-1 justify-self-end" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onOpen(d)} className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:bg-slate-800 hover:text-indigo-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400" title="Open" data-testid={`doc-list-open-${d.id}`}><Eye size={13} /></button>
        {canApprove && onApprove && (
          <button onClick={() => onApprove(d)} className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" title="Approve" data-testid={`doc-list-approve-${d.id}`}><CheckCircle2 size={13} /></button>
        )}
        {canGoodToGo && onGoodToGo && (
          <button onClick={() => onGoodToGo(d)} className="p-1.5 rounded-md text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/30" title="Good to Go" data-testid={`doc-list-good-${d.id}`}><CheckCircle2 size={13} /></button>
        )}
        {(canApprove || canGoodToGo) && onReject && (
          <button onClick={() => onReject(d)} className="p-1.5 rounded-md text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30" title="Reject" data-testid={`doc-list-reject-${d.id}`}><XCircle size={13} /></button>
        )}
        {context === "unassigned" && onReassign && (
          <button onClick={() => onReassign(d)} className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Reassign" data-testid={`doc-list-reassign-${d.id}`}><UserPlus size={13} /></button>
        )}
        {canResubmit && onResubmit && (
          <button onClick={() => onResubmit(d)} className="p-1.5 rounded-md text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/30" title="Re-Edit &amp; Resubmit" data-testid={`doc-list-resubmit-${d.id}`}><RefreshCcw size={13} /></button>
        )}
        {showPublish && onPublish && (
          <button onClick={() => onPublish(d)} className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30" title="Publish" data-testid={`doc-list-publish-${d.id}`}><Send size={13} /></button>
        )}
        {canEdit && onEdit && (
          <button onClick={() => onEdit(d)} className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:bg-slate-800 hover:text-indigo-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-indigo-400" title="Edit" data-testid={`doc-list-edit-${d.id}`}><Pencil size={13} /></button>
        )}
        {canDelete && onRemove && (
          <button onClick={() => onRemove(d)} className="p-1.5 rounded-md text-slate-500 dark:text-slate-400 hover:bg-rose-600 hover:text-white dark:text-slate-400" title="Delete" data-testid={`doc-list-delete-${d.id}`}><Trash2 size={13} /></button>
        )}
      </div>
    </div>
  );
}

// ---------- Chips ----------

function FilterChip({ active, onClick, count, children, testid }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-bold transition-all ${
        active
          ? "bg-gradient-to-br from-indigo-600 to-violet-700 border-indigo-600 text-white shadow-sm"
          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-indigo-300 hover:text-indigo-700 dark:hover:border-indigo-500 dark:hover:text-indigo-400"
      }`}
    >
      {children}
      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-slate-100 dark:bg-slate-800"}`}>{count}</span>
    </button>
  );
}

// Little red pending-count badge next to a sub-tab label.
function CountBadge({ n }) {
  if (!n || n <= 0) return null;
  return (
    <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5" data-testid="doc-count-badge">
      {n > 99 ? "99+" : n}
    </span>
  );
}

// ---------- Main page ----------

export default function Documents() {
  const { user } = useAuth();
  const currentUserId = user?.id;
  const isMaster = user?.role === "master_admin";

  const [accessRole, setAccessRole] = useState(null);
  const [topTab, setTopTab] = useState("documents");
  const [docSubTab, setDocSubTab] = useState("current");
  const [ecSubTab, setEcSubTab] = useState("mine");
  const [viewMode] = useGlobalViewMode(); // v107 — synced with the top-bar toggle

  const [currentItems, setCurrentItems] = useState([]);
  const [oldItems, setOldItems] = useState([]);
  const [ecItems, setEcItems] = useState([]);
  const [users, setUsers] = useState([]);

  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resubmitMode, setResubmitMode] = useState(false);
  const [showAccess, setShowAccess] = useState(false);
  const [detailDoc, setDetailDoc] = useState(null);
  const [detailContext, setDetailContext] = useState("feed");
  const [rejectDoc, setRejectDoc] = useState(null);
  const [reassignDoc, setReassignDoc] = useState(null);

  const [tagFilter, setTagFilter] = useState("all"); // all | proposal | guidelines | company_policy | other
  const [search, setSearch] = useState("");

  // Count badges for editor-corner sub-tabs.
  const [counts, setCounts] = useState({ approval: 0, review: 0, unassigned: 0, show_approval: false, show_review: false, show_unassigned: false });
  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/editor-corner/counts");
      setCounts(data || {});
    } catch { /* silent */ }
  }, []);

  // A ticking clock so scheduled/approval countdowns actually count down.
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const loadAccessRole = useCallback(async () => {
    try {
      const { data } = await api.get("/documents/access-role");
      setAccessRole(data.access_role);
    } catch { setAccessRole(null); }
  }, []);
  const loadCurrent = useCallback(async () => {
    try { const { data } = await api.get("/documents?bucket=current"); setCurrentItems(data || []); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadOld = useCallback(async () => {
    try { const { data } = await api.get("/documents?bucket=old"); setOldItems(data || []); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadEc = useCallback(async (sub) => {
    try { const { data } = await api.get(`/documents/editor-corner?sub=${sub}`); setEcItems(data || []); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  const loadUsers = useCallback(async () => {
    try { const { data } = await api.get("/directory"); setUsers(Array.isArray(data) ? data : []); }
    catch { /* silent */ }
  }, []);

  useEffect(() => { loadAccessRole(); loadCurrent(); loadUsers(); loadCounts(); }, [loadAccessRole, loadCurrent, loadUsers, loadCounts]);
  useEffect(() => { if (topTab === "documents" && docSubTab === "old") loadOld(); }, [topTab, docSubTab, loadOld]);
  useEffect(() => { if (topTab === "editor_corner") loadEc(ecSubTab); }, [topTab, ecSubTab, loadEc]);
  useEffect(() => { if (accessRole === null && topTab === "editor_corner") setTopTab("documents"); }, [accessRole, topTab]);
  // If editor lands on a tab they can't see, bounce to My Posts.
  useEffect(() => {
    if (topTab !== "editor_corner") return;
    if (ecSubTab === "approval" && !counts.show_approval && !isMaster) setEcSubTab("mine");
    if (ecSubTab === "review" && !counts.show_review && !isMaster) setEcSubTab("mine");
    if (ecSubTab === "unassigned" && !isMaster) setEcSubTab("mine");
  }, [topTab, ecSubTab, counts.show_approval, counts.show_review, isMaster]);

  const canPost = accessRole === "master_admin" || accessRole === "admin" || accessRole === "editor";
  const showEditorCorner = canPost;

  // Handlers
  const refreshAll = () => {
    loadCurrent(); loadCounts();
    if (topTab === "documents" && docSubTab === "old") loadOld();
    if (topTab === "editor_corner") loadEc(ecSubTab);
  };
  const onOpen = (d, ctx = "feed") => { setDetailContext(ctx); setDetailDoc(d); };
  const onEdit = (d) => { setEditing(d); setResubmitMode(false); setShowNew(true); };
  const onResubmit = (d) => { setEditing(d); setResubmitMode(true); setShowNew(true); };
  const [pendingDeleteDoc, setPendingDeleteDoc] = useState(null);
  const onRemove = (d) => setPendingDeleteDoc(d);
  const confirmDeleteDoc = async (reason) => {
    try {
      await api.delete(`/documents/${pendingDeleteDoc.id}`, { data: { reason } });
      toast.success("Document moved to Recycle Bin");
      refreshAll();
    } catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); throw e; }
  };
  const onPublish = async (d) => {
    try { await api.post(`/documents/${d.id}/approve`); toast.success("Approved"); refreshAll(); }
    catch (e) {
      // Fallback: if publish-now not applicable via approve, try direct approve/publish via PATCH
      try {
        await api.patch(`/documents/${d.id}`, { publish_now: "true" });
        toast.success("Published"); refreshAll();
      } catch { toast.error(formatError(e.response?.data?.detail || e.message)); }
    }
  };
  const onApprove = async (d) => {
    try { await api.post(`/documents/${d.id}/approve`); toast.success("Approved"); refreshAll(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };
  const onGoodToGo = async (d) => {
    try { await api.post(`/documents/${d.id}/reviewers/good-to-go`); toast.success("Marked Good to Go"); refreshAll(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail || e.message)); }
  };
  const onSaved = () => refreshAll();

  // Recompute countdowns from nowTick — we don't refetch every second, we
  // just subtract elapsed seconds from the server-side snapshot.
  const withLiveCountdowns = useCallback((items) => {
    const nowMs = nowTick;
    return items.map((d) => {
      const out = { ...d };
      const patch = (iso, key) => {
        if (!iso) return;
        const t = new Date(iso).getTime();
        if (Number.isFinite(t)) out[key] = Math.max(0, Math.floor((t - nowMs) / 1000));
      };
      if (d.status === "scheduled") patch(d.scheduled_for, "seconds_left_to_schedule");
      if (d.status === "pending_admin" && !d.is_important) patch(d.admin_deadline, "seconds_left_admin");
      if (d.status === "pending_master" && !d.is_important) patch(d.master_deadline, "seconds_left_master");
      return out;
    });
  }, [nowTick]);

  // Filter/search on Documents tab
  const rawItems = withLiveCountdowns(docSubTab === "current" ? currentItems : oldItems);
  const filteredDocs = useMemo(() => {
    const q = search.toLowerCase();
    return rawItems.filter((d) => {
      if (tagFilter !== "all" && d.tag !== tagFilter) return false;
      return !q || [d.title, d.description, d.tag_custom, d.created_by_name]
        .some((v) => String(v ?? "").toLowerCase().includes(q));
    });
  }, [rawItems, tagFilter, search]);

  const tagCounts = useMemo(() => {
    const c = { all: rawItems.length, proposal: 0, guidelines: 0, company_policy: 0, other: 0 };
    for (const d of rawItems) { if (c[d.tag] !== undefined) c[d.tag] += 1; }
    return c;
  }, [rawItems]);

  const ecItemsLive = withLiveCountdowns(ecItems);

  const renderGrid = (items, emptyMsg, ctx) => {
    if (items.length === 0) {
      return (
        <GradientCard tone="indigo" testid="doc-empty">
          <div className="py-10 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md mb-3"><Inbox size={22} className="text-white" /></div>
            <div className="text-base font-bold text-slate-800">No documents to show</div>
            <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">{emptyMsg}</div>
          </div>
        </GradientCard>
      );
    }
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="doc-grid">
        {items.map((d) => (
          <DocumentCard
            key={d.id} d={d} currentUserId={currentUserId} isMaster={isMaster}
            onOpen={(dd) => onOpen(dd, ctx || "feed")} onEdit={onEdit} onRemove={onRemove}
            onPublish={onPublish}
            onApprove={ctx === "approval" ? onApprove : undefined}
            onGoodToGo={ctx === "approval" ? onGoodToGo : undefined}
            onReject={ctx === "approval" ? (dd) => setRejectDoc(dd) : undefined}
            onReassign={ctx === "unassigned" ? (dd) => setReassignDoc(dd) : undefined}
            onResubmit={onResubmit}
            context={ctx || "feed"}
          />
        ))}
      </div>
    );
  };

  const renderList = (items, emptyMsg, ctx = "feed") => {
    if (items.length === 0) {
      return (
        <GradientCard tone="indigo" testid="doc-empty">
          <div className="py-8 text-center">
            <div className="text-base font-bold text-slate-800 dark:text-slate-100">No documents to show</div>
            <div className="text-[12px] text-slate-500 dark:text-slate-300 mt-1">{emptyMsg}</div>
          </div>
        </GradientCard>
      );
    }
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-transparent overflow-hidden" data-testid="doc-list">
        <div className="grid grid-cols-[minmax(0,3fr)_1fr_1fr_auto_auto_auto_auto] items-center gap-3 px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-transparent text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-white">
          <div>Title</div><div>Tag</div><div>Author</div><div>Status</div><div>Reads</div><div>Published</div><div className="justify-self-end">Actions</div>
        </div>
        {items.map((d) => (
          <ListRow
            key={d.id} d={d} currentUserId={currentUserId} isMaster={isMaster}
            onOpen={(dd) => onOpen(dd, ctx || "feed")} onEdit={onEdit} onRemove={onRemove}
            onPublish={onPublish} onResubmit={onResubmit}
            onApprove={(ctx === "approval") ? onApprove : undefined}
            onGoodToGo={(ctx === "review" || ctx === "approval") ? onGoodToGo : undefined}
            onReject={(ctx === "approval" || ctx === "review") ? (dd) => setRejectDoc(dd) : undefined}
            onReassign={ctx === "unassigned" ? (dd) => setReassignDoc(dd) : undefined}
            context={ctx}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="max-w-[1500px] space-y-6" data-testid="documents-page">
      <PageHeader
        moduleKey="documents"
        tone="indigo" icon={Folder} eyebrow="Knowledge Base"
        title="Documents Corner"
        subtitle="Files, links and policies shared across the team — with drafts, scheduling, approvals and read tracking."
      />

      <Tabs value={topTab} onValueChange={setTopTab}>
        <TabsList className={`grid ${showEditorCorner ? "grid-cols-2" : "grid-cols-1"} max-w-md`}>
          {tabAccessibleFor(user, "documents", "documents") && (
            <TabsTrigger value="documents" data-testid="doc-tab-documents">Documents</TabsTrigger>
          )}
          {showEditorCorner && tabAccessibleFor(user, "documents", "editor_corner") && (
            <TabsTrigger value="editor_corner" data-testid="doc-tab-editor-corner">Editor Corner</TabsTrigger>
          )}
        </TabsList>

        {tabAccessibleFor(user, "documents", "documents") && (
        <TabsContent value="documents" className="space-y-4">
          <Tabs value={docSubTab} onValueChange={setDocSubTab}>
            <TabsList>
              {subtabAccessibleFor(user, "documents", "documents", "current") && (
                <TabsTrigger value="current" data-testid="doc-sub-current">Current</TabsTrigger>
              )}
              {subtabAccessibleFor(user, "documents", "documents", "old") && (
                <TabsTrigger value="old" data-testid="doc-sub-old">Archived</TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          <div className="flex items-center gap-2 flex-wrap">
            <FilterChip active={tagFilter === "all"} onClick={() => setTagFilter("all")} count={tagCounts.all} testid="doc-filter-all">All</FilterChip>
            {TAG_ORDER.map((t) => (
              <FilterChip key={t} active={tagFilter === t} onClick={() => setTagFilter(t)} count={tagCounts[t]} testid={`doc-filter-${t}`}>
                {TAG_META[t].label}
              </FilterChip>
            ))}
            <div className="relative flex-1 min-w-[220px] max-w-md ml-auto">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents…" className="pl-9 h-10 bg-white" data-testid="doc-search" />
            </div>
          </div>

          {viewMode === "card"
            ? renderGrid(filteredDocs, search ? "Try a different search term." : (docSubTab === "current" ? "New shared documents will appear here." : "Archived documents will show up once they expire."), docSubTab === "current" ? "current" : "archived")
            : renderList(filteredDocs, search ? "Try a different search term." : (docSubTab === "current" ? "New shared documents will appear here." : "Archived documents will show up once they expire."), docSubTab === "current" ? "current" : "archived")}
        </TabsContent>
        )}

        {showEditorCorner && tabAccessibleFor(user, "documents", "editor_corner") && (
          <TabsContent value="editor_corner" className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Tabs value={ecSubTab} onValueChange={setEcSubTab}>
                <TabsList>
                  {subtabAccessibleFor(user, "documents", "editor_corner", "mine") && (
                    <TabsTrigger value="mine" data-testid="doc-ec-sub-mine">My Posts</TabsTrigger>
                  )}
                  {(counts.show_approval) && subtabAccessibleFor(user, "documents", "editor_corner", "approval") && (
                    <TabsTrigger value="approval" data-testid="doc-ec-sub-approval">
                      Approval<CountBadge n={counts.approval} />
                    </TabsTrigger>
                  )}
                  {counts.show_review && subtabAccessibleFor(user, "documents", "editor_corner", "review") && (
                    <TabsTrigger value="review" data-testid="doc-ec-sub-review">
                      Review Doc<CountBadge n={counts.review} />
                    </TabsTrigger>
                  )}
                  {counts.show_unassigned && subtabAccessibleFor(user, "documents", "editor_corner", "unassigned") && (
                    <TabsTrigger value="unassigned" data-testid="doc-ec-sub-unassigned">
                      Unassigned<CountBadge n={counts.unassigned} />
                    </TabsTrigger>
                  )}
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-2">
                {canPost && (
                  <Button
                    onClick={() => { setEditing(null); setResubmitMode(false); setShowNew(true); }}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="doc-ec-new-btn"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> New Document
                  </Button>
                )}
              </div>
            </div>
            {viewMode === "card"
              ? renderGrid(
                  ecItemsLive,
                  ecSubTab === "approval" ? "Nothing waiting on your approval right now." :
                  ecSubTab === "review" ? "Nothing pending your review or revision." :
                  ecSubTab === "unassigned" ? "All documents have active owners." :
                  "Nothing here yet. Click \"New Document\" to add one.",
                  ecSubTab
                )
              : renderList(
                  ecItemsLive,
                  ecSubTab === "approval" ? "Nothing waiting on your approval right now." :
                  ecSubTab === "review" ? "Nothing pending your review or revision." :
                  ecSubTab === "unassigned" ? "All documents have active owners." :
                  "Nothing here yet.",
                  ecSubTab
                )}
          </TabsContent>
        )}
      </Tabs>

      <NewDocDialog
        open={showNew}
        onClose={() => { setShowNew(false); setEditing(null); setResubmitMode(false); }}
        onSaved={onSaved}
        users={users}
        editing={editing}
        resubmitMode={resubmitMode}
        currentUserRole={user?.role || ""}
      />
      {isMaster && (
        <TabAccessDialog
          open={showAccess}
          onClose={() => setShowAccess(false)}
          users={users}
          onSaved={loadAccessRole}
        />
      )}
      <DocDetailDialog
        open={!!detailDoc}
        doc={detailDoc}
        onClose={() => { setDetailDoc(null); setDetailContext("feed"); refreshAll(); }}
        currentUserId={currentUserId}
        isMaster={isMaster}
        users={users}
        context={detailContext}
        onEdit={onEdit}
        onRemove={onRemove}
      />
      <RejectDialog
        open={!!rejectDoc} doc={rejectDoc}
        onClose={() => setRejectDoc(null)} onDone={refreshAll}
      />
      <ReassignDialog
        open={!!reassignDoc} doc={reassignDoc}
        onClose={() => setReassignDoc(null)} onDone={refreshAll}
      />
      <DeleteWithReasonDialog
        open={!!pendingDeleteDoc}
        onClose={() => setPendingDeleteDoc(null)}
        title={pendingDeleteDoc ? `Delete "${pendingDeleteDoc.title}"?` : "Delete document?"}
        description="It will move to Admin Hub → Recycle Bin. Master Admin can restore it later."
        onConfirm={confirmDeleteDoc}
        testid="doc-delete"
      />
    </div>
  );
}
